import {FC, useState, useEffect, useContext} from 'react';
import { api, useApi } from '../api/api';
import { Button, Container, Dimmer, Loader, Segment, SemanticCOLORS, Dropdown, Tab, Modal } from 'semantic-ui-react';
import Table, { TextCell, Cell, ActionsCell, ButtonCell } from '../components/Table';
import { HealthCondition, ScheduleRule } from '../api/health_monitor';
import { SyncButton } from '../components/buttons';
import { envColor } from '../common';
import {AuthContext} from "../components/Authentication";
import isAvailable from "../api/utils";


const AUTO_UPDATE_INTERVAL_SECS = 30;


const Loading = () => <Dimmer inverted active><Loader/></Dimmer>;


const severitySettings: { label: string, color: SemanticCOLORS }[] = [
    {label: "Clear", color: "green" },
    {label: "Harmless", color: "olive" },
    {label: "Warning", color: "yellow"},
    {label: "Error", color: "orange" },
    {label: "Urgent", color: "red" }
];


const DEFAULT_BASE_LEVEL = severitySettings.findIndex(item => item.label === "Warning");


const SeverityFilter: FC<{ level: number, onChange(_: number): void }> = ({ level, onChange }) => (
    <Dropdown
        button
        icon='filter'
        floating
        className='icon'
        labeled
        text={`Base Severity : ${severitySettings[level].label}`}
        options={severitySettings.map((item, index) => ({
            key: item.label,
            text: item.label,
            value: index,
            label: { color: item.color, circular: true, empty: true }
        }))}
        value={level}
        onChange={(_, { value }) => {
            if (typeof value === "number") {
                onChange(value);
            }
        }}
    />
);


const ConditionsView: FC<{ conditions: HealthCondition[], showRules: boolean }> = ({ conditions, showRules }) => {
    return (
        <Table rows={conditions}>
            <TextCell header='Name' value={r => r.name} />
            <TextCell header='Severity' value={r => severitySettings[r.severity + 1].label} />
            <TextCell header='Trigger' value={r => r.check_trigger} />
            <TextCell header='Last Check' value={r => r.last_check.substr(0, r.last_check.indexOf("."))} />
            {showRules ? <TextCell header='Rule' value={r => r.rule} /> : null}
            <TextCell header='Is Active Issue' value={r => r.is_active.toString()} />
        </Table>
    );
};


const ConditionsWindow: FC<{
    venue: any,
    onClose: () => void
}> = ({ venue, onClose }) => {
    return (
        <Modal open={true} size='large'>
            <Modal.Header>{venue.key}</Modal.Header>
            <Modal.Content>
                <ConditionsView conditions={venue.conditions} showRules={true} />
            </Modal.Content>
            <Modal.Actions>
                <Button circular onClick={onClose}>Close</Button>
            </Modal.Actions>
        </Modal>
    );
}


const getMaxSeverity = (conditions: HealthCondition[]) => {
    let severity = 0;

    conditions
        .filter(c => c.is_active)
        .forEach(c => {
            if (typeof c.severity === "number") {
                severity = Math.max(severity, c.severity + 1);
            }
        });

    return severity;
};


const StatusTable: FC<{ sources: Record<string, HealthCondition[]> }> = ({ sources }) => {
    const [selectedVenue, selectVenue] = useState();
    const resetVenue = () => selectVenue(undefined);

    const rows = Object.keys(sources).map(key => ({
        key,
        conditions: sources[key],
        severity: getMaxSeverity(sources[key]),
        status: severitySettings[getMaxSeverity(sources[key])]
    }));


    const statusCell = () => (
        <Cell
            collapsing
            header={"Status"}
            value={x => x}
            render={x => (
                <Button
                    size='tiny' collapsing
                    color={x.status.color} onClick={_ => selectVenue(x)}
                >
                    <p color={x.status.color}>{x.status.label}</p>
                </Button>
            )}
        />
    );

    return (
        <div style={{ marginTop: 10 }}>
            {selectedVenue &&
                <ConditionsWindow venue={selectedVenue} onClose={resetVenue} />
            }

            <Table rows={rows}>
                <ButtonCell header='Venue' icon='setting' value={x => x} label={x => x.key} onClick={selectVenue} />
                {statusCell()}
            </Table>
        </div>
    );
};


const MonitorResults: FC<{}> = () => {
    const healthMonitor = useApi(api.healthMonitor);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState<Set<string>>(new Set<string>());
    const [sources, setSources] = useState<Record<string, HealthCondition[]>>({});
    const [baseSeverity, setBaseSeverity] = useState<number>(DEFAULT_BASE_LEVEL);
    const [showRules, setShowRules] = useState(false);

    const toggleKey = (key: string) => {
        const newIndex = new Set(activeIndex);

        newIndex.has(key) ?
            newIndex.delete(key) : newIndex.add(key);

        setActiveIndex(newIndex);
    };

    const toggleRules = () => {
        setShowRules(!showRules);
    };

    const load = async (useLoadMsg: boolean) => {
        if (useLoadMsg) {
            setLoading(true);
        }

        const conditions = await healthMonitor.index();

        let sources: Record<string, HealthCondition[]> = {};
        conditions.forEach(x => {
            if (x.source === undefined) return;

            const source = x.source.split("/").slice(2).join("/");
            if (!(source in sources)) {
                sources[source] = [];
            }

            sources[source].push(x);
        });

        setSources(sources);
        if (useLoadMsg) {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(true).catch(console.error);

        const interval = setInterval(() => {
            console.log(`Auto-refresh each ${AUTO_UPDATE_INTERVAL_SECS} seconds`);
            load(false).catch(console.error);
        }, 1000 * AUTO_UPDATE_INTERVAL_SECS);

        return () => clearInterval(interval);
    }, []);

    let filteredSources: Record<string, HealthCondition[]> = {};

    filteredSources =
        Object.keys(sources)
        .filter(key => getMaxSeverity(sources[key]) >= baseSeverity)
        .reduce((output, key) => {
            output[key] = sources[key];
            return output;
        }, filteredSources);

    return (
        <Container>
            <Segment>
                <SyncButton load={load} color={envColor} />
                <SeverityFilter level={baseSeverity} onChange={setBaseSeverity} />

                {loading ?
                    <Loading /> :
                    <StatusTable sources={filteredSources} />
                }
            </Segment>
        </Container>
    );
}


const Schedule = () => {
    const healthMonitor = useApi(api.healthMonitor);
    const [loading, setLoading] = useState(false);
    const [rules, setRules] = useState<ScheduleRule[]>([]);

    const load = async () => {
        setLoading(true);

        const newRules = await healthMonitor.listRules();
        setRules(newRules);

        setLoading(false);
    };

    useEffect(() => {
        load().catch(console.error);
    }, []);

    if (loading) {
        return <Dimmer inverted active><Loader/></Dimmer>;
    }

    if (rules.length === 0) {
        return <Segment>Health check schedule is empty</Segment>
    }

    const actions = [
        {icon: "toggle on", onClick: async (rule: ScheduleRule) => {
            await healthMonitor.toggleRule(rule);
            await load();
        }}
    ];

    const ruleStatus = () => (
        <Cell
            header={"Is Enabled"}
            value={x => x}
            render={x => (
                <p style={{ color: x.is_enabled ? "green" : "red" }}>
                    {x.is_enabled.toString()}
                </p>
            )}
        />
    );

    return (
        <Container>
            <Segment>
                <Table rows={rules}>
                    <ActionsCell header='' actions={actions} />
                    <TextCell header='Expression' value={r => r.expression} />
                    {/* <TextCell header='Is Enabled' value={r => r.is_enabled.toString()} /> */}
                    {ruleStatus()}
                </Table>
            </Segment>
        </Container>
    );
};


const HealthMonitor = () => {
    const { user } = useContext(AuthContext);
    if (!user) {
        return <div>Not authenticated.</div>;
    }

    const panes = [
        {
            menuItem: { content: "Status", disabled: !isAvailable(["healthMonitor/index"], user) },
            render: () => <MonitorResults />,
        },
        {
            menuItem: { content: "Schedule", disabled: !isAvailable(["healthMonitor/list_rules"], user) },
            render: () => <Schedule />,
        },
    ];
    return (
        <Container>
            <Tab panes={panes} />
        </Container>
    );
};


export default HealthMonitor;
